<template>
  <div>
    <HttpContentView :endpoint="utilities.path('admin/communities/@', communityId)" v-model:reload="reload">
      <template v-slot:content="{ data }">
        <div class="row g-4">
          <div class="col-12 col-xl-6">
            <div class="card">
              <div class="card-body">
                <div class="row align-items-center g-4">
                  <div class="col-6">
                    <h4 class="mb-2">{{ translate("name") }}</h4>
                    <span>{{ data.name }}</span>
                  </div>
                  <div class="col-6">
                    <h4 class="mb-2">{{ translate("logo") }}</h4>
                    <div class="d-flex align-items-center g-3">
                      <img v-if="data.logoUrl" class="rounded-1" :class="{ clickable: authorization.communityAdministrator() }" :src="data.logoUrl" style="max-height: 1em; width: 1em" @click="authorization.communityAdministrator() ? editingName = true : undefined"/>
                      <a href="#" v-else-if="authorization.communityAdministrator()" class="bg-secondary text-primary clickable rounded-circle d-flex align-items-center justify-content-center" style="width: 2em; height: 2em" @click="editingName = true">
                        <i class="fas fa-upload"></i>
                      </a>
                    </div>
                  </div>
                  <div class="col-6">
                    <h4 class="mb-2">{{ translate("type") }}</h4>
                    <div class="d-flex align-items-center g-2">
                      <img class="img-fluid" :src="data.type.iconUrl" style="max-height: 16px; max-width: 16px"/>
                      <div>{{ data.type.name }}</div>
                    </div>
                  </div>
                  <div class="col-6">
                    <h4 class="mb-2">{{ translate("primary_identifier") }}</h4>
                    <div class="d-flex align-items-center g-2">
                      <i :class="data.primaryPlayerIdentifierType.icon"></i>
                      <div>{{ data.primaryPlayerIdentifierType.title }}</div>
                    </div>
                  </div>
                  <div class="col-6">
                    <h4 class="mb-2">{{ translate("player.panel.domain") }}</h4>
                    <a class="btn btn-link btn-primary" :href="'https://' + data.subdomain" target="_blank">
                      <div class="d-flex align-items-center g-2">
                        <span>{{ data.subdomain }}</span>
                        <i class="fas fa-external-link"></i>
                      </div>
                    </a>
                  </div>
                  <div v-if="data.domain" class="col-6">
                    <h4 class="mb-2">{{ translate("player.panel.custom.domain") }}</h4>
                    <a class="btn btn-link btn-primary" :href="'https://' + data.domain" target="_blank">
                      <div class="d-flex align-items-center g-2">
                        <span>{{ data.domain }}</span>
                        <i class="fas fa-external-link"></i>
                      </div>
                    </a>
                  </div>
                  <div class="col-12">
                    <ButtonView v-if="authorization.adminRole(adminRoles.superAdmin) || data.owner.id === info.user?.id" type="soft" color="primary" class="w-100" @click="editingName= true">
                      <div class="d-flex justify-content-center align-items-center g-2">
                        <i class="fas fa-edit"></i>
                        <span>{{ translate("edit") }}</span>
                      </div>
                    </ButtonView>
                    <ModalView v-model:active="editingName">
                      <template v-slot:body>
                        <HttpFormView :content="{ name: data.name, domain: data.domain, subdomain: data.subdomain.split('.')[0], logoIdentifier: data.logoIdentifier, logoUrl: data.logoUrl }" :endpoint="utilities.path('admin/communities/@', data.id)" method="put" @success="editingName = false; communityModified($event)">
                          <template v-slot:form="{ content, errors, submit, loading }">
                            <div class="d-flex flex-column g-4">
                              <InputView type="text" :title="translate('name')" :max="24" v-model:value="content.name" :errors="errors.name" required/>
                              <div class="d-flex align-items-center g-3">
                                <InputView type="text" :title="translate('player.panel.domain')" :max="30" v-model:value="content.subdomain" :errors="errors.subdomain" required/>
                                <div style="white-space: nowrap">.{{ state.frontendDomain }}</div>
                              </div>
                              <InputView type="text" :title="translate('player.panel.custom.domain')" :max="30" v-model:value="content.domain" :errors="errors.domain"/>
                              <div v-if="content.domain && content.domain.length > 0" class="alert alert-info">
                                <div class="d-flex align-items-center g-3">
                                  <i class="fas fa-exclamation-circle text-info"></i>
                                  <div>
                                    <div class="mb-3">{{ translate("community.domain.custom.validation.explanation") }}</div>
                                    <span class="bg-secondary rounded-3 p-2">{{ content.domain }} CNAME communities.{{ state.frontendDomain }}</span>
                                  </div>
                                </div>
                              </div>
                              <FileInputView :title="translate('logo')" :extensions="logoExtensions" :endpoint="utilities.path('admin/communities/@/logo', communityId)" method="post" :value="content.logoIdentifier ? { identifier: content.logoIdentifier, url: content.logoUrl } : undefined" @update:value="content.logoIdentifier = $event?.identifier; content.logoUrl = $event?.url" :errors="errors.file" required/>
                              <div class="d-flex align-items-center g-4">
                                <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                                  {{ translate("save") }}
                                </ButtonView>
                                <ButtonView type="normal" color="secondary" class="w-100" @click="editingName = false">
                                  {{ translate("cancel") }}
                                </ButtonView>
                              </div>
                              <SpinnerView :active="loading" color="primary"/>
                            </div>
                          </template>
                        </HttpFormView>
                      </template>
                    </ModalView>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-xl-6">
            <div class="list-group" v-if="authorization.communityAdministrator()">
              <div v-for="usageId in ['modules.core.team.members', 'modules.servers.serverCount', 'modules.core.storage.space', 'modules.automation.tasks.count']" :key="usageId" class="list-group-item">
                <UsageView :id="usageId"/>
              </div>
            </div>
          </div>
        </div>
      </template>
    </HttpContentView>
    <ModalView :active="tutorialActive === 'setup'">
      <template v-slot:header>
        <h3 class="mb-0">{{ translate("setup") }}</h3>
      </template>
      <template v-slot:body>
        <SetupTutorialView :community-id="communityId" @complete="tutorialActive = undefined;"/>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {adminRoles} from "@/systems/main/adminRoles.js";
import authorization from "@/authorization";
import info from "@/info";
import {computed, ref} from "vue";
import {ButtonView, FileInputView, HttpContentView, HttpFormView, InputView, ModalView, SpinnerView, utilities} from "@/niftybit";
import {state} from "@/state";
import UsageView from "@/systems/usage/UsageView.vue";
import SetupTutorialView from "@/systems/community/views/SetupTutorialView.vue";

defineProps({
  communityId: String
});

const reload = ref(false);
const editingName = ref(false);

const tutorialActive = utilities.routeQueryParam("tutorial");

function communityModified() {
  info.reload();
  info.reloadCommunity();
  reload.value = true;
}

const logoExtensions = computed(() => {
  return ['jpg', 'jpeg', 'png', 'gif'];
});

</script>
